import { useParams, Navigate } from "react-router-dom";
import { PageHero, Reveal, usePageMeta } from "@/components/primitives";
import { CONTACT_EMAIL } from "@/lib/data";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    sections: [
      { h: "What We Collect", b: "When you submit an enquiry on this website we collect your name, organisation, email address, phone number, area of interest and message — solely to respond to your enquiry." },
      { h: "How We Use It", b: "We use submitted details only to respond to and manage your enquiry. We do not sell, rent or share your personal information with third parties for their marketing." },
      { h: "Retention & Your Rights", b: `Enquiry data is retained only as long as needed to handle your request and legitimate follow-up. To access, correct or delete your data, write to us at ${CONTACT_EMAIL}.` },
      { h: "Our Product Commitment", b: "TimePe is being architected with privacy by design — data minimisation, consent-first flows and purpose limitation are defaults in the platform we are building, and they apply to this website too." },
    ],
  },
  terms: {
    title: "Terms of Use",
    sections: [
      { h: "Informational Purpose", b: "This website describes Avyagrah Tech Innovations Private Limited and its flagship platform TimePe, which is under active development. Content is provided for general information only." },
      { h: "Forward-Looking Statements", b: "Statements about planned or future capabilities, targets and roadmap phases are forward-looking, reflect current intentions and are subject to change without notice." },
      { h: "No Offer", b: "Nothing on this website constitutes an offer of securities, investment advice, or the provision of regulated financial services." },
      { h: "Intellectual Property", b: "The Avyagrah and TimePe names, logos and content on this site are the property of Avyagrah Tech Innovations Private Limited and may not be reproduced without written permission." },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    sections: [
      { h: "Development Status", b: "TimePe is under active development. Capabilities described as in development, planned or future are aspirational and may change in scope, timing or nature." },
      { h: "Targets, Not Traction", b: "Any figures presented on this website as targets or vision — including agent network size and user reach — are forward-looking goals, not achieved results." },
      { h: "No Unverified Claims", b: "We do not claim certifications, licences or regulatory approvals we do not hold. Institutional affiliations are limited to those explicitly listed on this website." },
      { h: "External Links & Content", b: "We are not responsible for the content or practices of external sites that may reference or link to this website." },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    sections: [
      { h: "What We Use", b: "This website uses minimal cookies and privacy-conscious analytics to understand aggregate usage and improve the experience. We do not use cookies to build advertising profiles." },
      { h: "Your Control", b: "You can control or delete cookies through your browser settings at any time. The site remains fully usable with cookies disabled." },
      { h: "Changes", b: `If our use of cookies changes, this page will be updated. Questions can be sent to ${CONTACT_EMAIL}.` },
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const d = DOCS[doc];
  usePageMeta(d ? `${d.title} | Avyagrah Tech Innovations` : "Legal | Avyagrah Tech Innovations");
  if (!d) return <Navigate to="/" replace />;
  return (
    <>
      <PageHero eyebrow="Legal" lines={[d.title]} lead="Last updated: July 2026." />
      <section className="py-20 sm:py-24" data-testid={`legal-${doc}`}>
        <div className="mx-auto max-w-3xl space-y-10 px-6">
          {d.sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.05}>
              <h2 className="font-display text-xl font-semibold text-mist">{s.h}</h2>
              <p className="mt-3 text-sm leading-relaxed text-mute">{s.b}</p>
            </Reveal>
          ))}
          <Reveal delay={0.1}>
            <p className="text-xs leading-relaxed text-mute/70">
              Contact: {CONTACT_EMAIL} — Avyagrah Tech Innovations Private Limited.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
